import {
  Box,
  Flex,
  Heading,
  Text,
  Skeleton,
  useToast,
  Link,
  HStack,
  Spacer,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  SimpleGrid,
  Tooltip,
  Tag,
  TagLabel,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import {
  useParams,
  useNavigate,
  Link as ReactRouterLink,
} from 'react-router-dom';
import { z } from 'zod';

import {
  date as commonDate,
  utils as commonUtils,
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';

import { DeliverableContainerReceiptActionTypeGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { getDeliverableDetailViewUrl } from '../utils';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { DownloadActionButton } from '~/core/routes/deliverables/[deliverableId]/DownloadActionButton';
import { DeliverableReceiptsInputTable } from '~/core/routes/deliverables/[deliverableId]/final-output-report-versions/[receiptId]/DeliverableReceiptsInputTable';
import { DigitalMonthlyReportContainer } from '~/core/routes/deliverables/[deliverableId]/final-output-report-versions/[receiptId]/digitalMonthlyReport/DigitalMonthlyReportContainer';
import { useCreateDeliverableContainerReceiptActionMutation } from '~/core/routes/deliverables/[deliverableId]/final-output-report-versions/[receiptId]/mutations/CreateDeliverableContainerReceiptAction.gql.generated';
import {
  GetDeliverableContainerReceiptsDocumentGQLType,
  useGetDeliverableContainerReceiptsQuery,
} from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableContainerReceipts.gql.generated';
import { SingleEntityDrawer as DeliverableEventLogSingleEntityDrawer } from '~/core/routes/deliverables/EntityEventLogDrawers';
import {
  CommentEditorInput,
  CommentEditorCancelButton,
  CommentEditorCreateButton,
  CommentEditor,
} from '~/packages/comments/CommentEditor';
import { useCreateThreadWithCommentMutation } from '~/packages/comments/mutations/CreateThreadWithComment.gql.generated';
import { useGetThreadsByDomainIdentifierQuery } from '~/packages/comments/queries/GetThreadsByDomainIdentifier.gql.generated';
import { TagIcon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { ColorScheme } from '~/packages/common-frontend/theme';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';

// Types
const RouteParamsStruct = z.object({
  deliverableId: z.string({ message: 'Missing URL parameter' }),
  receiptId: z.string({ message: 'Missing URL parameter' }),
});

type ReceiptStatus =
  | 'readyToSubmit'
  | 'submitted'
  | 'approved'
  | 'remediationRequired';

// Constants
const receiptStatusColor: { [key in ReceiptStatus]: ColorScheme } = {
  readyToSubmit: 'blue',
  submitted: 'blue',
  approved: 'green',
  remediationRequired: 'red',
};

const receiptStatusIcon: { [key in ReceiptStatus]: string } = {
  readyToSubmit: 'circle-check',
  submitted: 'mailbox-flag-up',
  approved: 'circle-check',
  remediationRequired: 'messages-question',
};

// Helpers
const calculateReceiptStatus = (
  action: DeliverableContainerReceiptActionTypeGQLType,
): ReceiptStatus => {
  switch (action) {
    case 'generated':
      return 'readyToSubmit';
    case 'submitted':
      return 'submitted';
    case 'approved':
      return 'approved';
    case 'requestRemediation':
      return 'remediationRequired';
    default:
      return finleyAssertUnreachable(action);
  }
};

// Fragments
type ReceiptStatusBadgeProps = {
  mostRecentAction: DeliverableContainerReceiptActionTypeGQLType;
};
const ReceiptStatusBadge = ({ mostRecentAction }: ReceiptStatusBadgeProps) => {
  const status = calculateReceiptStatus(mostRecentAction);
  const colorScheme = receiptStatusColor[status];

  return (
    <Tag variant="solid-muted" colorScheme={colorScheme} flexShrink={0}>
      <TagIcon icon={receiptStatusIcon[status]} />
      <TagLabel>{commonUtils.toTitleCase(status)}</TagLabel>
    </Tag>
  );
};

type MarkReadyForReviewModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (comment: string) => void;
};
const MarkReadyForReviewModal = ({
  isOpen,
  onCancel,
  onSubmit,
}: MarkReadyForReviewModalProps) => {
  const cancelRef = React.useRef(null);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
      size="xl"
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <CommentEditor onCreate={onSubmit}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Mark Ready For Review
            </AlertDialogHeader>
            <AlertDialogBody display="flex" flexDirection="column" gap="1rem">
              <Typography variant="base-muted">
                Are you sure you want to share this report with the capital
                provider?
              </Typography>
              <CommentEditorInput isEditable placeHolderText="Optional" />
            </AlertDialogBody>

            <AlertDialogFooter display="flex" flexDirection="row" gap="1rem">
              <CommentEditorCancelButton
                size="md"
                isDisabled={false}
                onCancel={onCancel}
              >
                Cancel
              </CommentEditorCancelButton>
              <CommentEditorCreateButton size="md" isDisabled={false}>
                Submit for Review
              </CommentEditorCreateButton>
            </AlertDialogFooter>
          </CommentEditor>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

const RouteBase = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { deliverableId, receiptId } = RouteParamsStruct.parse(params);
  const toast = useToast();

  finleyAssert(receiptId, 'receiptId is required');
  finleyAssert(deliverableId, 'deliverableId is required');

  const {
    data: receiptsData,
    error: receiptsError,
    refetch: refetchReceipts,
  } = useGetDeliverableContainerReceiptsQuery({
    fetchPolicy: 'network-only',
    variables: {
      containerId: deliverableId,
    },
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const [
    /**
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    createDeliverableContainerReceiptActionMutation,
  ] = useCreateDeliverableContainerReceiptActionMutation({
    fetchPolicy: 'network-only',
    refetchQueries: [
      {
        query: GetDeliverableContainerReceiptsDocumentGQLType,
        variables: { containerId: deliverableId },
      },
    ],
    onError(error) {
      errorToastConfig.title = commonUtils.EnvUtils.isCustomerFacing()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : error.message;
      toast(errorToastConfig);
    },
  });
  const [
    isMarkForReviewModalOpen,
    setIsMarkForReviewModalOpen,
  ] = useState<boolean>(false);

  const threadsQuery = useGetThreadsByDomainIdentifierQuery({
    variables: {
      domain: 'deliverables_container',
      domainIdentifier: deliverableId,
    },
  });

  const [createThreadWithComment] = useCreateThreadWithCommentMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });
  const commentsDrawerDisclosure = useDisclosure({ defaultIsOpen: false });

  const { user } = useUserAuthOrThrow();

  if (threadsQuery.error) throw threadsQuery.error;
  if (!receiptsData || !threadsQuery.data) {
    return (
      <Skeleton
        height="600px"
        data-testid="deliverables-final-output-report-versions-view-loading"
      />
    );
  }

  if (receiptsData.deliverableContainerReceipts.length === 0) {
    toast({ title: 'No existing versions.' });
    navigate(`${getDeliverableDetailViewUrl(deliverableId)}`);
  }

  if (receiptsError) {
    toast({ title: `Failed to load page.` });
    navigate('/deliverables');
  }

  const receipts = receiptsData.deliverableContainerReceipts;
  const currentReceipt = receipts.find((r) => r.id === receiptId);

  if (!currentReceipt) {
    toast({ title: 'Could not find version.' });
    navigate(`${getDeliverableDetailViewUrl(deliverableId)}`);
    return (
      <Skeleton
        height="600px"
        data-testid="deliverables-final-output-report-versions-view-error"
      />
    );
  }

  const detailViewTitle = `${commonUtils.toTitleCase(
    currentReceipt.containerName,
  )} for ${commonDate.fullDateStrToShortMonthDayYearString(
    currentReceipt.containerDueDate,
  )}`;

  return (
    <Box height="100%">
      <Flex justify="space-between" flexDirection="column">
        <Text fontSize="xs" color="gray.800" letterSpacing="wide" pb="16px">
          <Link as={ReactRouterLink} to="/deliverables" fontWeight="semibold">
            Deliverables
          </Link>
          {' > '}
          <Link
            as={ReactRouterLink}
            to={`/deliverables/${deliverableId}`}
            fontWeight="semibold"
          >
            {detailViewTitle}
          </Link>{' '}
          {`> Report Version ${currentReceipt.version}`}
        </Text>
        <HStack spacing="20px">
          <Heading as="h2" fontSize="3xl">
            {`${commonUtils.toTitleCase(
              currentReceipt.containerName,
            )} Version ${
              currentReceipt.version
            } - ${commonDate.fullDateStrToShortMonthDayString(
              currentReceipt.containerDueDate,
            )}`}
          </Heading>
          {currentReceipt.expired ? (
            <Tooltip
              label="A more recent version has been submitted to the Capital Provider."
              aria-label="expired-tooltip"
              placement="right"
            >
              <Tag variant="solid-muted" colorScheme="gray" flexShrink={0}>
                <TagIcon icon="calendar-circle-exclamation" />
                <TagLabel>Expired Version</TagLabel>
              </Tag>
            </Tooltip>
          ) : (
            <ReceiptStatusBadge
              mostRecentAction={currentReceipt.actionsLog[0].action}
            />
          )}

          <Spacer />
          <DeliverableEventLogSingleEntityDrawer
            entityId={deliverableId}
            receiptId={receiptId}
            disclosure={commentsDrawerDisclosure}
            externalOrgOptions={currentReceipt.capitalProviderEntities.map(
              (obj) => ({
                value: obj.id,
                label: obj.name,
              }),
            )}
          />
          <DownloadActionButton currentReceipt={currentReceipt} />
          <Button
            whiteSpace="normal"
            variant="primary"
            colorScheme="v2-aurora"
            isDisabled={
              currentReceipt.expired ||
              currentReceipt.actionsLog[0].action !== 'generated'
            }
            onClick={() => {
              setIsMarkForReviewModalOpen(true);
            }}
          >
            {currentReceipt.actionsLog[0].action === 'generated'
              ? 'Submit to Capital Provider'
              : 'Submitted to Capital Provider'}
          </Button>
        </HStack>

        <HStack mt={2} mb={2}>
          {!currentReceipt.expired &&
          currentReceipt.actionsLog[0].action !== 'generated' ? (
            <>
              <Spacer />
              <Box maxWidth="240px">
                <Text
                  fontSize="xs"
                  color="gray.500"
                >{`Version ${currentReceipt.version} has been shared with your capital provider.`}</Text>
              </Box>
            </>
          ) : (
            <Box mb={8} />
          )}
        </HStack>

        <SimpleGrid columns={3} spacingX={10} spacingY="4px" mb={8}>
          <Text fontSize="xs" color="gray.600">
            Credit Facility
          </Text>
          <Text fontSize="xs" color="gray.600">
            Deliverable Type
          </Text>
          <Text fontSize="xs" color="gray.600">
            Due Date
          </Text>

          <Text as="b" fontSize="sm">
            {currentReceipt.creditFacilityName}
          </Text>
          <Text as="b" fontSize="sm">
            {commonUtils.toTitleCase(currentReceipt.containerType)}
          </Text>
          <Text as="b" fontSize="sm">
            {commonDate.fullDateStrToHumanDateString(
              currentReceipt.containerDueDate,
            )}
          </Text>
        </SimpleGrid>

        {currentReceipt.containerType === 'settlementReport' &&
        currentReceipt.finalArtifactsInstances.length > 0 ? (
          <DigitalMonthlyReportContainer currentReceipt={currentReceipt} />
        ) : (
          <>
            <Heading as="h2" fontSize="24px" mb="24px">
              Inputs
            </Heading>
            <DeliverableReceiptsInputTable currentReceipt={currentReceipt} />
          </>
        )}

        <MarkReadyForReviewModal
          isOpen={isMarkForReviewModalOpen}
          onCancel={() => {
            setIsMarkForReviewModalOpen(false);
          }}
          onSubmit={async (comment: string) => {
            let commentId: string | undefined;
            if (comment.length > 0) {
              const {
                data: newThreadWithCommentData,
              } = await createThreadWithComment({
                variables: {
                  participatingOrganizationIds: [
                    currentReceipt.borrowerId,
                    ...currentReceipt.capitalProviderEntities.map(
                      (obj) => obj.id,
                    ),
                  ],
                  comment,
                  domain: 'deliverables_container',
                  domainIdentifier: deliverableId,
                },
              });
              finleyAssert(
                newThreadWithCommentData &&
                  newThreadWithCommentData.thread.comments.length > 0,
                `[Borrower] Missing newThreadWithCommentData for mutation [createNewThreadWithCommentForDeliverableContainerMutation] [threadId: ${newThreadWithCommentData?.thread.threadId}]`,
              );
              commentId = newThreadWithCommentData.thread.comments[0].id;

              // do not need to call notifyNewCommentForDeliverableContainerMutation here because, comment is sent as part of "ready for review email"
              threadsQuery.refetch();
            }

            // mutation
            await createDeliverableContainerReceiptActionMutation({
              variables: {
                userId: user.id,
                organizationId: user.organization.id,
                receiptId: currentReceipt.id,
                action: 'submitted',
                commentId,
              },
            });

            // refetch receipts
            refetchReceipts();

            // close modal
            setIsMarkForReviewModalOpen(false);
          }}
        />
      </Flex>
    </Box>
  );
};

// Exports
export const Route = () => <RouteBase />;
